import { html, TemplateElement, toString } from '@webtides/element-js';
import { unsafeHTML } from 'lit-html/directives/unsafe-html';
import BuyBoxEvents from './BuyBoxEvents';
import style from './buy-box.css';
import '../../../elements/paypal-messages/paypal-messages';
import '../../_buy-box/buy-box-variants/buy-box-variants';
import '../../_product/product-price/product-price';
import '../../../elements/glide-element/glide-element';
import GlideElementEvents from '../../../elements/glide-element/GlideElementEvents';
import '../../../elements/_modal/image-modal/image-modal';
import '../../../elements/pan-zoom/pan-zoom';
import { getSrcByBreakpoint } from '../../../elements/prod-image/prod-image';
import SelectedSpecs from '../SelectedSpecs';
import VariantStore from '../VariantStore';
import FrameSelectorEvent from '../../frame-selector/FrameSelectorEvent';
import ImageModalEvent from '../../../elements/_modal/image-modal/ImageModalEvent';
import './product-cta/product-cta';
import { getSalePriceValue } from '../../../../js/helper/price-model-helper';
import { classMap } from 'lit-html/directives/class-map.js';
import clamp from '../../../../js/helper/clamp';
import { getSrcByDimension } from '../../../elements/tessa-image/tessa-image';

export default class BuyBoxClass extends TemplateElement {
    properties() {
        return {
            product: {},
            disableRetailerSearch: false,
            selectedSpecs: null,
            selectedVariant: null,
            currentColorIndex: 0,
            showPrice: true,
            showSubline: false,
            subline: '',
            backgroundImage: null,
            priceLink: 'null',
            infoBox: false,
            isShop: true,
            showPaypalInfobox: true,
            stageAnimation: true,
        };
    }

    constructor(options) {
        super({ shadowRender: false, styles: [style], ...options });
        this._lastMouseDownEvent = null;
    }

    connected() {
        this.initProduct();
        // decodes html escaped snippet from freemarker template, to avoid quotes clashes!
        this.priceLink = decodeURIComponent(this.priceLink);
        this.infoBox = decodeURIComponent(this.infoBox || '');
    }

    get availabilities() {
        return this.selectedVariant?.availabilities ?? false;
    }

    get hasFrameSelector() {
        return Object.keys(this.frames).length > 1 && this.selectedVariant.specs.hasOwnProperty('RAICO');
    }

    get currentFrameId() {
        return this.selectedVariant?.specs.RAICO.attributeKey;
    }

    get currentFrame() {
        if (this.selectedVariant?.specs.RAICO) {
            return this.frames[this.currentFrameId];
        }
    }

    get availableColors() {
        if (!this.product.specOptions['FARB']) return [];
        return Object.keys(this.product.specOptions['FARB'].values);
    }

    get images() {
        return this.availableColors.map(availableColor => {
            const { variant } = VariantStore.getFirstAvailableProductVariantAndSpecsInColor(
                availableColor,
                this.selectedSpecs
            );

            return {
                image: variant.image,
                alt: this.getProductVariantAlt(variant),
            };
        });
    }

    get isCentralDelivery() {
        return window.siteConfig().isCentralDelivery || false;
    }

    get isBrand() {
        return window.siteConfig().isBrand || false;
    }

    get ctaClass() {
        return 'btn btn-accent is-hero';
    }

    get seal() {
        return this.product?.reviews[0] ?? false;
    }

    get comparisonEnabled() {
        return window.siteConfig()?.comparisonEnabled ?? false;
    }

    get defaultPromotionLogo() {
        return `${window.siteConfig().publicPath}/assets/images/promotion_logo_default.svg`;
    }

    get promotionLogo() {
        return this.product?.promotionLogo ?? this.defaultPromotionLogo ?? false;
    }

    initProduct() {
        if (!this.product || Object.keys(this.product).length === 0) {
            console.warn('Kein Produkt gefunden.');
            return;
        }

        VariantStore.set(this.product.variants);

        if (!this.selectedVariant) {
            this.selectedVariant = this.product.selectedVariant || VariantStore.getFirst();
        } else {
            this.selectedVariant = VariantStore.getVariantBySpecCombination(this.selectedVariant.specCombinationKey);
        }

        // set initial selectedSpecs and holds selection state
        const initialSpecCombination = this.selectedVariant.specCombinationKey;

        this.selectedSpecs = new SelectedSpecs(initialSpecCombination);

        this.currentColorIndex = this._initialColorIndex = this.getCurrentColorIndex();
    }

    watch() {
        return {
            currentColorIndex: colorIndex => {
                let currentSelectedColor = this.availableColors[colorIndex];
                const clonedSpecs = this.selectedSpecs.clone();
                // select color matching the sliders current index
                clonedSpecs.set('FARB', currentSelectedColor);
                // update Specs to align spec options with slider
                this.onSpecChange('FARB', clonedSpecs);
                // align slider index with the actual variant
                this.$refs.imageSlider.api.go(`=${colorIndex}`);
            },
            selectedVariant: selectedVariant => {
                this.dispatch(BuyBoxEvents.VARIANT_CHANGE, selectedVariant, true);
            },
            product: () => this.initProduct(),
        };
    }

    events() {
        return {
            // only register scroll listener, if stageAnimation is true
            ...(this.stageAnimation
                ? {
                      document: {
                          scroll: () => {
                              this.handleScroll();
                          },
                      },
                  }
                : {}),
            this: {
                mousedown: e => {
                    this._lastMouseDownEvent = e;
                },
                [ImageModalEvent.PREV]: () => {
                    if (this.currentColorIndex < this.availableColors.length - 1) {
                        this.$refs.imageSlider.next();
                    }
                },
                [ImageModalEvent.NEXT]: () => {
                    if (this.currentColorIndex > 0) {
                        this.$refs.imageSlider.prev();
                    }
                },
            },
            '[ref=variants]': {
                'specs-changed': e => {
                    const { spec, selectedSpecs } = e.detail;
                    this.onSpecChange(spec, selectedSpecs);
                },
            },
            '[ref=arrowNext]': {
                click: ev => {
                    if (this.currentColorIndex < this.availableColors.length - 1) {
                        this.$refs.imageSlider.next();
                    }
                },
            },
            '[ref=arrowPrev]': {
                click: ev => {
                    if (this.currentColorIndex > 0) {
                        this.$refs.imageSlider.prev();
                    }
                },
            },
            '[open-detail]': {
                click: () => {
                    this.$refs.detailModal.open();
                },
            },
            '[ref=imageSlider]': {
                [GlideElementEvents.GLIDE_RUN]: e => {
                    // set index straight from glide as it represents direct interactions of the user with the slider
                    this.currentColorIndex = clamp(0, e.detail ?? 0, this.availableColors.length - 1);
                },
            },
            '.slide-img': {
                click: e => {
                    var distance = 10;
                    if (
                        Math.abs(this._lastMouseDownEvent.screenX - e.screenX) < distance &&
                        Math.abs(this._lastMouseDownEvent.screenY - e.screenY) < distance
                    ) {
                        this.$refs.detailModal.open();
                    }
                    this._lastMouseDownEvent = null;
                },
            },
            'frame-selector': {
                [FrameSelectorEvent.FRAME_CLICK]: ev => {
                    const url = this.frames[ev.detail];
                    if (url) {
                        window.location.href = url;
                    }
                },
            },
        };
    }

    handleScroll() {
        if (document.body.scrollHeight >= window.innerHeight * 1.5) {
            const animationDelay = Math.max(0, Math.min(100, (window.scrollY - 10) / 5));
            this.style.setProperty('--animation-delay', `-${animationDelay}s`);
        }

        if (window.scrollY > 10) {
            this.setAttribute('has-scrolled', true);
        } else {
            this.removeAttribute('has-scrolled');
        }
    }

    onSpecChange(changedSpec, selectedSpecs) {
        const { variant, specs } = VariantStore.getClosestExistingVariant(changedSpec, selectedSpecs);
        this.selectedVariant = variant;
        this.selectedSpecs = specs;
        // update color index that slider can eventually adjust to color changes caused by variant changes
        this.currentColorIndex = this.getCurrentColorIndex();

        if (!this.selectedVariant) {
            console.error('### variant missing', this.selectedSpecs.toString());
        }
    }

    getCurrentColorIndex() {
        return this.availableColors.findIndex(row => row === this.selectedSpecs.get('FARB'));
    }

    getProductVariantAlt(variant) {
        const color = variant.specs.FARB?.value ?? '';
        const frame = variant.specs.RAICO?.value ?? '';
        const brand = this.product.brand ? `${this.product.brand} - ` : '';

        return `${brand}${this.product.name} ${frame.toUpperCase()} ${color}`;
    }

    get showAvailabilitySearch() {
        return (
            this.isShop &&
            !this.disableRetailerSearch &&
            this.isCentralDelivery === false &&
            // TODO: Must be validated depending on the button state. the button in the retailer search also needs to be re-evaluated due to the revised CTA functionality.
            this.selectedVariant?.available
        );
    }

    templateDivider() {
        return '';
    }

    templateBackgroundImage() {
        if (!this.backgroundImage) {
            return '';
        }

        return html` <div class="absolute inset-0 overflow-hidden">${unsafeHTML(this.backgroundImage)}</div> `;
    }

    templatePaypalMessage() {
        if (!this.isShop || !this.showPaypalInfobox || !this.selectedVariant?.price) {
            return '';
        }

        return html`
            <paypal-messages
                class="block lg:self-stretch"
                amount="${getSalePriceValue(this.selectedVariant.prices) / 100}"
            ></paypal-messages>
        `;
    }

    templateProductTitle() {
        return html`
            ${this.product.brand && !this.isBrand
                ? html` <div class="product-brand rfs-text text-base no-rfs-margin">${this.product.brand}</div>`
                : ''}
            <div class="product-title rfs-text text-xl no-rfs-margin">${this.product.name}</div>
        `;
    }

    templateProductAvailability() {
        if (!this.availabilities) {
            return html``;
        }

        return html`
            <product-availability
                class="block mt-6"
                .availabilities="${this.availabilities}"
                .showAvailabilitySearch="${this.showAvailabilitySearch}"
            ></product-availability>
        `;
    }

    templateInfoBox() {
        if (!this.infoBox) {
            return html``;
        }

        return html`
            <div class="info-box bg-grey-100 text-grey-700 text-sm px-4 py-2 mt-6">
                ${this.infoBox.headline ? html` <strong class="block mb-1">${this.infoBox.headline}</strong> ` : html``}

                <p class="leading-tight">${this.infoBox.text} ${unsafeHTML(this.infoBox.link ?? '')}</p>
            </div>
        `;
    }

    templateActions() {
        return html`
            ${this.comparisonEnabled
                ? html`<comparison-add-link sku="${this.selectedVariant.sku}"></comparison-add-link>`
                : html``}
        `;
        /*
        HINT: removes share function in the course of ticket OHH-2165!
        <modal-trigger class="flex items-center" target-modal="social-share">
            <div class="w-10 h-10 flex items-center justify-center">
                <svg-icon class="inline-block w-4 h-4" name="share"></svg-icon>
            </div>
            <span>${i18n('buybox.share', 'Teilen')}</span>
        </modal-trigger>
        */
    }

    templatePromotionLogo() {
        return html` <img class="promotion-logo absolute right-0" src="${this.promotionLogo}" alt="Promotion Logo" /> `;
    }

    getImageByProvider({ source, url }) {
        let detailImage;
        switch (source) {
            case 'PROD_IMAGE':
                const detailBreakpoint = { from: 0, width: 2400, height: 1600 };
                detailImage = getSrcByBreakpoint(url, detailBreakpoint);
                break;
            default:
                detailImage = getSrcByDimension({ url: url, width: 2400, height: 1600 });
        }
        return detailImage;
    }

    template() {
        if (!this.selectedVariant) return html``;

        const breakPoints = [
            { from: 0, width: 352, height: 235 },
            { from: 768, width: 460, height: 307 },
            { from: 992, width: 570, height: 380 },
            { from: 1200, width: 970, height: 546 },
        ];

        // const detailImage = getSrcByBreakpoint(this.selectedVariant.image, detailBreakpoint);
        const detailImageAlt = this.getProductVariantAlt(this.selectedVariant);
        const detailImage = this.getImageByProvider(this.selectedVariant.image);

        const titleContainerCssClasses = {
            'has-price': this.showPrice,
            'has-frame-selector': this.hasFrameSelector,
        };

        return html`
            <div class="scroll-animation-wrapper ${classMap({ 'disabled-animation': !this.stageAnimation })}">
                ${this.templateBackgroundImage()}
                <div class="product-section">
                    <div
                        class="title-container md:flex items-start justify-between ${classMap(
                            titleContainerCssClasses
                        )}"
                    >
                        ${this.hasFrameSelector
                            ? html`
                                  <frame-selector
                                      class="frame-selector inline-block order-last md:absolute md:right-0"
                                      frames="${toString(Object.keys(this.frames))}"
                                      active-frame="${this.currentFrameId}"
                                  ></frame-selector>
                              `
                            : ''}
                        <div class="product-title-wrapper w-full md:pr-1/3 md:pointer-events-none lg:pr-1/5">
                            ${this.templateProductTitle()}
                            <div class="product-meta-information w-full md:absolute">
                                ${this.showSubline && this.subline
                                    ? html` <div class="product-description w-full truncate">${this.subline}</div> `
                                    : ''}
                                ${this.showPrice
                                    ? html`
                                          <product-price
                                              class="block product-price-small ${!this.selectedVariant?.price
                                                  ? 'invisible'
                                                  : ''}"
                                              .prices="${this.selectedVariant?.prices}"
                                          ></product-price>
                                      `
                                    : ''}
                                ${this.seal && this.seal.iconUrl
                                    ? html`
                                          <a
                                              target="${this.seal.document ? '_blank' : '_self'}"
                                              href="${this.seal.document ?? '#'}"
                                              class="absolute block seal ${this.seal.document
                                                  ? ''
                                                  : 'pointer-events-none'}"
                                          >
                                              <img class="seal-img h-auto" src="${this.seal.iconUrl}" />
                                          </a>
                                      `
                                    : ''}
                                ${this.product?.promotionLogo ? this.templatePromotionLogo() : ''}
                            </div>
                        </div>
                    </div>
                    <div class="slider-container reset-container relative mt-2">
                        <div class="slider-container-content flex items-center relative z-0">
                            <glide-element
                                bullets="false"
                                arrows="false"
                                class="block w-full"
                                ref="imageSlider"
                                options='{"type":"slider", "perView":1,
                                           "startAt":${this._initialColorIndex}, "rewind":false}'
                            >
                                ${this.images.map(({ image, alt }, index) =>
                                    image.source === 'TESSA'
                                        ? html`
                                              <div class="slide-element" slot="slide-${index}">
                                                  <tessa-image
                                                      url="${image?.url}"
                                                      class="slide-img relative block cursor cursor-pointer item"
                                                      img-css-class="object-scale-down w-full h-full"
                                                  ></tessa-image>
                                              </div>
                                          `
                                        : html`
                                              <div class="slide-element" slot="slide-${index}">
                                                  <prod-image
                                                      class="slide-img relative block cursor"
                                                      breakpoints="${toString(breakPoints)}"
                                                      src="${image?.url}"
                                                      alt="${alt}"
                                                      picture-class="block w-full h-full"
                                                      img-class="object-scale-down w-full h-full"
                                                  ></prod-image>
                                              </div>
                                          `
                                )}
                            </glide-element>

                            <div class="arrow-prev-container  w-1/12 flex ">
                                ${this.availableColors.length > 1
                                    ? html`
                                          <div
                                              class="arrow cursor-pointer flex justify-center items-center text-center p-2 ${this
                                                  .currentColorIndex <= 0
                                                  ? 'is-inactive'
                                                  : ''}"
                                              ref="arrowPrev"
                                          >
                                              <svg-icon name="prev"></svg-icon>
                                          </div>
                                      `
                                    : ''}
                            </div>

                            <div class="arrow-next-container w-1/12 flex ">
                                ${this.availableColors.length > 1
                                    ? html`
                                          <div
                                              class="arrow flex justify-center items-center cursor-pointer text-center p-2
                                            ${this.currentColorIndex >= this.availableColors.length - 1
                                                  ? 'is-inactive'
                                                  : ''}"
                                              ref="arrowNext"
                                          >
                                              <svg-icon name="next"></svg-icon>
                                          </div>
                                      `
                                    : ''}
                            </div>
                        </div>

                        <div
                            class="zoom-container flex flex-col justify-center items-center cursor-pointer absolute left-0 right-0 bottom-0 pb-8 md:flex-row md:h-6"
                            open-detail
                        >
                            <svg-icon class="zoom-icon" name="zoom_plus"></svg-icon>
                            <div class="hidden md:block md:absolute md:right-4 lg:right-8">
                                ${this.templateActions()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            ${this.templateDivider()}
            <div class="variant-section">
                <div class="variant-section-container relative lg:flex justify-between">
                    <div class="absolute right-0 top-0 md:hidden">${this.templateActions()}</div>
                    <buy-box-variants
                        ref="variants"
                        class="xl:w-1/2 border-b border-grey-200 pb-4 md:border-0"
                        spec-options="${toString(this.product.specOptions)}"
                        spec-options-links="${toString(this.product.specOptionsLinks)}"
                        .selectedSpecs="${this.selectedSpecs}"
                    ></buy-box-variants>
                    <div class="product-cta">
                        ${this.selectedVariant?.price &&
                        html`
                            <product-price
                                type="big"
                                .prices="${this.selectedVariant.prices}"
                                link-html="${this.priceLink}"
                            ></product-price>
                        `}
                        <product-cta
                            product="${toString(this.product)}"
                            selected-variant="${toString(this.selectedVariant)}"
                            cta-class-fallback="${this.ctaClass}"
                        ></product-cta>
                        ${this.templatePaypalMessage()} ${this.templateProductAvailability()} ${this.templateInfoBox()}
                    </div>
                </div>
            </div>

            <image-modal
                modal-id="modal-detail"
                ref="detailModal"
                container="false"
                update-hash="false"
                img-src="${detailImage}"
                img-alt="${detailImageAlt}"
            ></image-modal>
        `;
    }
}
